import { z } from "zod";
import { BusinessDate, Instant, Version } from "./common.js";
import { Id } from "./ids.js";

export const REPORT_IDS = [
  "daily-exceptions",
  "waste-and-returns",
  "returnable-containers",
  "delivery-discrepancies",
  "late-changes",
] as const;

export type ReportId = (typeof REPORT_IDS)[number];

export const REPORT_METADATA: Record<ReportId, { title: string; description: string }> = {
  "daily-exceptions": {
    title: "استثناهای روز",
    description: "تغییرات سفارش، واحدهای ثبت‌نشده و اقلام خارج از منو",
  },
  "waste-and-returns": {
    title: "ضایعات و مرجوعی",
    description: "حواله‌های ضایعات و مرجوعی کالا با ذکر دلیل و سفارشات برگشتی",
  },
  "returnable-containers": {
    title: "ظروف امانی",
    description: "مانده و گردش ظروف امانی به تفکیک واحد و فهرست واحدهای بدهکار",
  },
  "delivery-discrepancies": {
    title: "اختلاف تحویل و شکایت‌ها",
    description: "مغایرت سفارش و تحویل و گزارش مشکلات ثبت‌شده توسط رابطین",
  },
  "late-changes": {
    title: "تغییرات دیرهنگام و برگشت‌ها",
    description: "سفارش‌های دیرهنگام، درخواست‌های تغییر بعد از قفل و تصمیمات مدیر",
  },
};

export const ReportRoleVisibility = z.object({
  id: Id,
  roleId: z.string().min(1),
  reportId: z.enum(REPORT_IDS),
  allowed: z.boolean().default(true),
  version: Version.default(0),
  createdAt: Instant.optional(),
  updatedAt: Instant.optional(),
});
export type ReportRoleVisibility = z.infer<typeof ReportRoleVisibility>;

export const SetReportVisibilityInput = z.object({
  roleId: z.string().min(1),
  reportId: z.enum(REPORT_IDS),
  allowed: z.boolean(),
});
export type SetReportVisibilityInput = z.infer<typeof SetReportVisibilityInput>;

// --- 1. Daily Exceptions Report ---
export const DailyExceptionsReport = z.object({
  from: BusinessDate,
  to: BusinessDate,
  changes: z.array(
    z.object({
      orderId: Id,
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      businessDate: BusinessDate,
      requestedByName: z.string(),
      status: z.string(),
      note: z.string(),
      decidedAt: Instant.nullable().optional(),
      decisionNote: z.string().optional(),
    }),
  ),
  unregisteredUnits: z.array(
    z.object({
      date: BusinessDate,
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
    }),
  ),
  offMenuOrders: z.array(
    z.object({
      orderId: Id,
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      businessDate: BusinessDate,
      menuItemName: z.string(),
      quantity: z.number().int(),
      note: z.string(),
    }),
  ),
  summary: z.object({
    totalChanges: z.number().int(),
    totalUnregistered: z.number().int(),
    totalOffMenu: z.number().int(),
  }),
});
export type DailyExceptionsReport = z.infer<typeof DailyExceptionsReport>;

// --- 2. Waste and Returns Report ---
export const WasteAndReturnsReport = z.object({
  from: BusinessDate,
  to: BusinessDate,
  inventoryWaste: z.array(
    z.object({
      voucherId: Id,
      voucherNumber: z.string().nullable().optional(),
      date: BusinessDate,
      warehouseName: z.string(),
      itemName: z.string(),
      itemCode: z.string(),
      baseUnit: z.string(),
      quantityMilli: z.number().int(),
      reason: z.string(),
      note: z.string().optional(),
      unitPriceRial: z.number().int().nullable().optional(),
    }),
  ),
  supplierReturns: z.array(
    z.object({
      voucherId: Id,
      voucherNumber: z.string().nullable().optional(),
      date: BusinessDate,
      warehouseName: z.string(),
      itemName: z.string(),
      itemCode: z.string(),
      baseUnit: z.string(),
      quantityMilli: z.number().int(),
      reason: z.string(),
      note: z.string().optional(),
    }),
  ),
  orderReturns: z.array(
    z.object({
      orderId: Id,
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      businessDate: BusinessDate,
      returnNote: z.string(),
      status: z.string(),
    }),
  ),
  summary: z.object({
    totalWasteVouchers: z.number().int(),
    totalSupplierReturns: z.number().int(),
    totalOrderReturns: z.number().int(),
  }),
});
export type WasteAndReturnsReport = z.infer<typeof WasteAndReturnsReport>;

// --- 3. Returnable Containers Report ---
export const ReturnableContainersReport = z.object({
  asOfDate: BusinessDate,
  containerTypes: z.array(
    z.object({
      id: Id,
      code: z.string(),
      name: z.string(),
    }),
  ),
  unitBalances: z.array(
    z.object({
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      balances: z.record(z.string(), z.number().int()), // containerCode -> net balance
      totalNetContainers: z.number().int(),
    }),
  ),
  debtorUnits: z.array(
    z.object({
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      debtSummary: z.string(),
      totalNetContainers: z.number().int(),
    }),
  ),
  summary: z.object({
    totalTrackedUnits: z.number().int(),
    totalDebtorUnits: z.number().int(),
    totalOutstandingContainers: z.number().int(),
  }),
});
export type ReturnableContainersReport = z.infer<typeof ReturnableContainersReport>;

// --- 4. Delivery Discrepancies and Complaints Report ---
export const DeliveryDiscrepanciesReport = z.object({
  from: BusinessDate,
  to: BusinessDate,
  discrepancies: z.array(
    z.object({
      dispatchId: Id.nullable().optional(),
      businessDate: BusinessDate,
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      orderedQuantity: z.number().int(),
      deliveredQuantity: z.number().int(),
      diffQuantity: z.number().int(),
      status: z.string(),
      note: z.string().optional(),
    }),
  ),
  complaints: z.array(
    z.object({
      problemId: Id,
      businessDate: BusinessDate,
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      kind: z.string(),
      note: z.string(),
      recordedAt: Instant,
      resolvedAt: Instant.nullable().optional(),
      resolutionNote: z.string().nullable().optional(),
    }),
  ),
  summary: z.object({
    totalDiscrepancyStops: z.number().int(),
    totalShortPortions: z.number().int(),
    totalComplaints: z.number().int(),
    unresolvedComplaints: z.number().int(),
  }),
});
export type DeliveryDiscrepanciesReport = z.infer<typeof DeliveryDiscrepanciesReport>;

// --- 5. Late Changes and Returns Report ---
export const LateChangesReport = z.object({
  from: BusinessDate,
  to: BusinessDate,
  lateOrders: z.array(
    z.object({
      orderId: Id,
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      businessDate: BusinessDate,
      submittedAt: Instant.nullable().optional(),
      enteredByStaff: z.boolean(),
      totalQuantity: z.number().int(),
      status: z.string(),
    }),
  ),
  changeRequests: z.array(
    z.object({
      requestId: Id,
      orderId: Id,
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      businessDate: BusinessDate,
      requestedByName: z.string(),
      status: z.string(),
      note: z.string(),
      decidedByName: z.string().nullable().optional(),
      decidedAt: Instant.nullable().optional(),
      decisionNote: z.string().optional(),
      portionsDifference: z.number().int(),
    }),
  ),
  returnedOrders: z.array(
    z.object({
      orderId: Id,
      unitId: Id,
      unitName: z.string(),
      orgName: z.string(),
      businessDate: BusinessDate,
      returnNote: z.string(),
      status: z.string(),
    }),
  ),
  summary: z.object({
    totalLateOrders: z.number().int(),
    totalChangeRequests: z.number().int(),
    acceptedChangeRequests: z.number().int(),
    rejectedChangeRequests: z.number().int(),
    totalReturnedOrders: z.number().int(),
  }),
});
export type LateChangesReport = z.infer<typeof LateChangesReport>;
